import type { createApplicationStartup } from '../../../shared/application-startup';

interface BootstrapOptions {
  document: Document;
  root: HTMLElement;
  startup: ReturnType<typeof createApplicationStartup>;
  loadingText: () => string;
  failureText: () => string;
  retryText: () => string;
  retry: () => void;
}

/** Keeps React mounted at its real size beneath the existing startup screen. */
export const createBrowserBootstrap = ({
  document, root, startup, loadingText, failureText, retryText, retry,
}: BootstrapOptions) => {
  const shell = document.getElementById('app-bootstrap')!;
  let revision = 0;

  const show = (message: string, onRetry?: () => void) => {
    revision += 1;
    document.documentElement.dataset.appLoading = 'true';
    root.setAttribute('aria-hidden', 'true');
    root.setAttribute('inert', '');
    shell.hidden = false;
    shell.replaceChildren();
    const mark = document.createElement('div');
    mark.className = 'app-bootstrap__mark';
    mark.textContent = '☰';
    mark.setAttribute('aria-hidden', 'true');
    const text = document.createElement('p');
    text.textContent = message;
    shell.append(mark, text);
    if (onRetry) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = retryText();
      button.addEventListener('click', onRetry, { once: true });
      shell.append(button);
    }
  };

  const sync = () => {
    const status = startup.getStatus();
    if (status !== 'ready') {
      show(status === 'failed' ? failureText() : loadingText(), status === 'failed' ? retry : undefined);
      return;
    }
    const expectedRevision = ++revision;
    // Fonts and two layout/paint turns must settle after the last startup task.
    Promise.resolve(document.fonts?.ready).then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (expectedRevision !== revision || startup.getStatus() !== 'ready') return;
        startup.markRevealed();
        delete document.documentElement.dataset.appLoading;
        root.removeAttribute('aria-hidden');
        root.removeAttribute('inert');
        shell.hidden = true;
      }));
    }).catch(() => {
      if (expectedRevision === revision) show(failureText(), retry);
    });
  };

  const unsubscribe = startup.subscribe(sync);
  return { show, dispose: () => { revision += 1; unsubscribe(); } };
};
